<template>
  <v-container fluid>
    <page-header showBackButton>
      <template slot="botRightSlot" v-if="id !== null">
        <div class="d-flex">
          <v-btn
            depressed
            class="ml-2 mb-4"
            elevation="0"
            color="error"
            :disabled="$store.getters.isLoading"
            @click="deleteCompany()"
            >{{ $t('deleteCompany') }}</v-btn
          >
          <v-btn
            depressed
            class="ml-2 mb-4"
            elevation="0"
            color="primary"
            :disabled="$store.getters.isLoading"
            :to="{ name: 'CompanyOrderList', params: { cid: id } }"
            >{{ $t('viewOrder') }}</v-btn
          >
          <v-btn
            depressed
            class="ml-2 mb-4"
            elevation="0"
            color="primary"
            type="button"
            @click="openAddServiceForm()"
            :disabled="$store.getters.isLoading"
            >{{ $t('addService') }}</v-btn
          >
        </div>
      </template>
    </page-header>

    <!-- Company Basic Form -->
    <v-card class="mb-8">
      <v-card-text>
        <v-form ref="basicForm" @submit.prevent>
          <v-row class="ma-0">
            <v-col cols="12" sm="6" lg="3">
              <form-select
                label="companyStatus"
                :fieldValue.sync="formData.company_status"
                :options="companyStatusOptions"
                required
              />
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <form-select
                label="companyType"
                :fieldValue.sync="formData.type"
                :options="$companyTypeOptions"
                required
                :disabled="uid === null && formData.type != ''"
              />
            </v-col>
          </v-row>
          <v-row class="ma-0">
            <v-col cols="12" sm="6" lg="3">
              <form-input label="companyNameEn" :fieldValue.sync="formData.name_en" required :disabled="disableEdit" />
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <form-input label="companyNameZh" :fieldValue.sync="formData.name_zh" :disabled="disableEdit" />
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <form-input label="contactNameZh" :fieldValue.sync="formData.contact_zh" :disabled="disableEdit" />
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <form-input label="contactNameEn" :fieldValue.sync="formData.contact_en" :disabled="disableEdit" />
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <form-select
                label="gender"
                :fieldValue.sync="formData.gender"
                :options="$genderOptions"
                :disabled="disableEdit"
              />
            </v-col>
            <v-col cols="12" sm="6" lg="3" class="pa-0">
              <v-row class="ma-0 pa-0">
                <v-col cols="4" class="pr-0">
                  <form-select :fieldValue.sync="formData.contact_zone" :options="$phoneCodeOptions" />
                </v-col>
                <v-col cols="8">
                  <form-input
                    label="contactMobile"
                    :fieldValue.sync="formData.contact_tel"
                    type="tel"
                    :customRules="[v => !v || $validate.isMobile(v, formData.contact_zone, false)]"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <form-input
                label="companyEmail"
                :fieldValue.sync="formData.contact_email"
                type="email"
                :disabled="disableEdit"
              />
            </v-col>
            <v-col cols="12" sm="12" lg="6">
              <form-input label="contactAddress" :fieldValue.sync="formData.mail_address" :disabled="disableEdit" />
            </v-col>

            <template v-if="formData.type === 'limited'">
              <v-col cols="12" sm="6" lg="3">
                <form-input
                  label="totalShare"
                  :fieldValue.sync="formData.total_share"
                  type="number"
                  :disabled="disableEdit"
                />
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <form-input
                  label="pricePerShare"
                  :fieldValue.sync="formData.price_per"
                  type="number"
                  prefix="HK$"
                  :disabled="disableEdit"
                />
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <form-input
                  label="totalSharePrice"
                  :fieldValue="totalSharePrice"
                  readonly
                  prefix="HK$"
                  :disabled="disableEdit"
                />
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <form-select
                  label="fundSource"
                  :fieldValue.sync="formData.fund_source"
                  :options="fundSourceOptions"
                  :disabled="disableEdit"
                />
              </v-col>
              <v-col cols="12" sm="6" lg="3" v-if="formData.fund_source === 'others'">
                <form-input
                  label="fundSourceOther"
                  :fieldValue.sync="formData.fund_source_other"
                  required
                  :disabled="disableEdit"
                />
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <form-select
                  label="fundSourceCountry"
                  :fieldValue.sync="formData.fund_source_country"
                  :options="$countryOptions"
                  :disabled="disableEdit"
                />
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <form-input
                  label="businessDescription"
                  :fieldValue.sync="formData.business_description"
                  :disabled="disableEdit"
                />
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <form-input
                  label="mainProductDescription"
                  :fieldValue.sync="formData.main_product_service"
                  :disabled="disableEdit"
                />
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <form-select
                  label="businessLocation"
                  :fieldValue.sync="formData.main_business_location"
                  :options="$countryOptions"
                  :disabled="disableEdit"
                />
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <form-select
                  label="turnover"
                  :fieldValue.sync="formData.expected_annual_income"
                  :options="turnoverOptions"
                  :disabled="disableEdit"
                />
              </v-col>
            </template>
            <template v-if="formData.type === 'unlimit'">
              <v-col cols="12" class="pa-0 mb-4">
                <v-row class="ma-0">
                  <v-col cols="12">
                    <label>{{ $t('businessNature') }}</label>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.accounting')"
                      value="accounting"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.it')"
                      value="it"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.architecture')"
                      value="architecture"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.marketing')"
                      value="marketing"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.realestate')"
                      value="realestate"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.trading')"
                      value="trading"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.education')"
                      value="education"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.wholesale')"
                      value="wholesale"
                    ></v-checkbox>
                    <form-input
                      v-if="formData.business_type.includes('wholesale')"
                      :fieldValue.sync="formData.sale_string"
                      required
                      dense
                      placeholder="wholesaleProductDes"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.finance')"
                      value="finance"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.retail')"
                      value="retail"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.insurance')"
                      value="insurance"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.manufacture')"
                      value="manufacture"
                    ></v-checkbox>
                    <form-input
                      v-if="formData.business_type.includes('manufacture')"
                      :fieldValue.sync="formData.made_string"
                      required
                      dense
                      placeholder="wholesaleProductDes"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.restaurant')"
                      value="restaurant"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.logistics')"
                      value="logistics"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="2">
                    <v-checkbox
                      v-model="formData.business_type"
                      class="custom-checkbox sm"
                      :label="$t('businessNatureOptions.other')"
                      value="other"
                    ></v-checkbox>
                    <form-input
                      v-if="formData.business_type.includes('other')"
                      :fieldValue.sync="formData.other_string"
                      required
                      dense
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <form-date-picker
                  label="companyOpenDate"
                  :fieldValue.sync="formData.business_start_date"
                  disabledActionButtons
                  manualInput
                  disableFuture
                  :disabled="disableEdit"
                />
              </v-col>
            </template>

            <template v-if="formData.type === 'limited' || formData.type === 'unlimit'">
              <v-col cols="12" sm="6" lg="3">
                <form-select
                  label="companyRegAddressType"
                  :fieldValue.sync="formData.register_address_type"
                  :options="regAddressTypeOptions"
                  :disabled="disableEdit"
                  @changed="handleRegAddressTypeChanged()"
                />
              </v-col>
              <v-col cols="12" sm="12" lg="6">
                <form-input
                  label="companyRegAddress"
                  :fieldValue.sync="formData.register_address"
                  :disabled="disableEdit"
                />
              </v-col>

              <v-col cols="12" sm="6" lg="3">
                <form-date-picker
                  label="companyEstablishDate"
                  :fieldValue.sync="formData.company_start_at"
                  disabledActionButtons
                  manualInput
                  disableFuture
                  :disabled="disableEdit"
                />
              </v-col>
            </template>

            <template v-if="formData.type === 'group_assoc'">
              <v-col cols="12" sm="6" lg="3">
                <form-input
                  label="organizationNumber"
                  :fieldValue.sync="formData.acco_number"
                  required
                  :disabled="disableEdit"
                />
              </v-col>
              <v-col cols="12">
                <image-uploader
                  inputRef="uploadAcco"
                  label="uploadOrganizationFile"
                  :fileValue.sync="formData.acco_file"
                  fileType="imgWithPDF"
                />
              </v-col>
            </template>
            <template v-else>
              <v-col cols="12" sm="6" lg="3" class="pa-0">
                <v-row class="ma-0 pa-0">
                  <v-col cols="8" class="pr-0">
                    <form-input
                      label="brNumber"
                      :fieldValue.sync="formData.br_number_prefix"
                      type="number"
                      isIntegerOnly
                      :customRules="[v => !v || $validate.regex('^[0-9]{8}$', v) || $t('form.brNumberInv')]"
                      @input-value="handleCompanyBrPrefixInput($event)"
                      @onkeypress="handleCompanyBrPrefixKeypress($event)"
                      :disabled="disableEdit"
                    >
                      <template slot="append-outer">-</template>
                    </form-input>
                  </v-col>
                  <v-col cols="4">
                    <form-input
                      :fieldValue.sync="formData.br_number_suffix"
                      type="number"
                      isIntegerOnly
                      :customRules="[v => !v || $validate.regex('^[0-9]{3}$', v) || $t('form.brNumberInv')]"
                      inputRef="companyBrNumSuffixInput"
                      ref="companyBrNumSuffix"
                      :disabled="disableEdit"
                      :required="formData.br_number_prefix != ''"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12">
                <image-uploader
                  inputRef="uploadCompanyBr"
                  label="uploadBrFile"
                  :fileValue.sync="formData.br_file"
                  fileType="imgWithPDF"
                />
              </v-col>
            </template>
          </v-row>
        </v-form>

        <v-btn
          v-if="id !== null"
          depressed
          elevation="0"
          class="ml-2 mb-4"
          color="primary"
          type="button"
          @click="saveCompany()"
          :loading="$store.getters.isLoading"
          >{{ $t('save') }}</v-btn
        >
      </v-card-text>
    </v-card>

    <!-- 公司文件 -->
    <v-card class="mb-8" v-if="id !== null">
      <v-card-text>
        <v-row class="ma-0">
          <v-col cols="12">
            <h4 class="section-title">{{ $t('companyDoc') }}</h4>
          </v-col>
          <v-col cols="12">
            <datatable
              :isLoading="companyDocTableLoading"
              :tableHeaders="companyDocTableHeaders"
              :tableData="companyDocTableData"
              disableToolbar
              disablePagination
              disableShadow
            >
              <template v-slot:[`item.item_status`]="{ item }">
                <div class="d-flex align-center justify-space-between" v-if="!item.updateStatus">
                  <span
                    v-if="item.item_status"
                    class="mr-2"
                    :class="{
                      'success--text': item.item_status === 'valid',
                      'warning--text': item.item_status === 'expiring_soon',
                      'error--text': item.item_status === 'expired',
                    }"
                    >{{ $t(`docStatusOption.${item.item_status}`) }}</span
                  >
                  <v-btn
                    v-if="item.status !== 'expired' && (item.validity_date || item.complete_date)"
                    depressed
                    class="my-2"
                    elevation="0"
                    color="primary"
                    type="button"
                    small
                    outlined
                    @click="item.updateStatus = true"
                    :disabled="$store.getters.isLoading"
                    >{{ $t('update') }}</v-btn
                  >
                </div>
                <div class="d-flex align-center justify-space-between" v-else>
                  <div style="width: 115px; max-width: 115px" class="mr-2">
                    <form-select
                      :fieldValue.sync="item.update_status"
                      :options="companyDocStatusOptions"
                      dense
                      hideDetails
                    />
                  </div>
                  <v-btn
                    depressed
                    class="my-2"
                    elevation="0"
                    color="primary"
                    type="button"
                    small
                    outlined
                    @click="saveDocumentStatus(item.type)"
                    :disabled="$store.getters.isLoading"
                    :loading="$store.getters.isLoading"
                    >{{ $t('save') }}</v-btn
                  >
                </div>
              </template>
              <template v-slot:[`item.action`]="{ item }">
                <div class="d-flex align-center justify-end flex-wrap">
                  <v-btn
                    depressed
                    class="ml-2 my-2"
                    elevation="0"
                    color="primary"
                    type="button"
                    small
                    @click="viewDocumentFile(item.file)"
                    v-if="item.file"
                    >{{ $t('view') }}</v-btn
                  >
                  <v-btn
                    depressed
                    class="ml-2 my-2"
                    elevation="0"
                    color="primary"
                    type="button"
                    small
                    @click="triggerDocUpload(item.type)"
                    :loading="$store.getters.uploading"
                    >{{ $t('uploadDoc') }}</v-btn
                  >
                  <v-btn
                    depressed
                    class="ml-2 my-2"
                    elevation="0"
                    color="primary"
                    type="button"
                    small
                    style="min-width: 110px;"
                    @click="openUpdateValidDate(item.type)"
                    v-if="item.type != 'ci'"
                    >{{ $t(item.type === 'duty' ? 'updateCompletedDate' : 'updateValidityDate') }}</v-btn
                  >
                </div>
              </template>
            </datatable>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 其他公司文件 -->
    <v-card class="mb-8" v-if="formData.type === 'limited' && id !== null">
      <v-card-text>
        <v-row class="ma-0">
          <v-col cols="12">
            <h4 class="section-title">{{ $t('otherDoc') }}</h4>
          </v-col>
          <v-col cols="12">
            <datatable
              :isLoading="otherDocTableLoading"
              :tableHeaders="otherDocTableHeaders"
              :tableData="otherDocTableData"
              disableToolbar
              disablePagination
              disableShadow
              @view-file-clicked="viewDocumentFile($event)"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 公司成員 -->
    <v-card class="mb-8">
      <v-card-text>
        <v-row class="ma-0">
          <v-col cols="12" class="d-flex justify-space-between align-center">
            <h4 class="section-title">{{ $t('companyMember') }}</h4>
            <v-btn
              depressed
              class="ml-2 mb-4"
              elevation="0"
              color="primary"
              type="button"
              :disabled="$store.getters.isLoading || !formData.type"
              @click="openAddMemberForm()"
              >{{ $t('addMember') }}</v-btn
            >
          </v-col>
          <v-col cols="12">
            <datatable
              :tableHeaders="memberTableHeaders"
              :tableData="memberTableData"
              disableToolbar
              disablePagination
              disableShadow
              @delete-clicked="deleteMember($event)"
              @view-clicked="openAddMemberForm('edit', $event)"
            >
              <template v-slot:[`item.member_type`]="{ item }">
                <span v-if="formData.type === 'unlimit'">{{
                  $t(`unlimitedMemberTypeOptions.${item.member_type}`)
                }}</span>
                <span v-else>{{ $t(`memberTypeOptions.${item.member_type}`) }}</span>
              </template>
              <template v-slot:[`item.title`]="{ item }">{{
                item.title ? $t(`titleOptions.${item.title}`) : ''
              }}</template>
              <template v-slot:[`item.phone`]="{ item }">{{
                item.contact_tel ? `(${item.contact_zone}) ${item.contact_tel}` : ''
              }}</template>
              <template v-slot:[`item.name_en`]="{ item }">{{ `${item.first_name} ${item.last_name}` }}</template>
              <template v-slot:[`item.identity`]="{ item }">{{ $t(`identityOptions.${item.identity}`) }}</template>
              <template v-slot:[`item.gender`]="{ item }">{{
                item.gender ? $t(`genderOptions.${item.gender}`) : ''
              }}</template>
              <template v-slot:[`item.pep`]="{ item }">{{ item.pep | formatYesNo }}</template>
              <template v-slot:[`item.owner`]="{ item }">{{ item.owner | formatYesNo }}</template>
              <template v-slot:[`item.id_type`]="{ item }">{{
                item.id_type ? $t(`idTypeOptions.${item.id_type}`) : ''
              }}</template>
              <template v-slot:[`item.sign_country`]="{ item }">{{ item.sign_country | formatCountry }}</template>
              <template v-slot:[`item.reg_country`]="{ item }">{{ item.reg_country | formatRegCountry }}</template>
              <template v-slot:[`item.verified`]="{ item }">
                <div v-if="!item.updateVerified" class="d-flex align-center justify-space-between">
                  <span v-if="item.verified" class="success--text">{{ $t(`identityVerifyOptions.verified`) }}</span>
                  <span v-else-if="item.member_type === 'personal'" class="error--text">{{
                    $t(`identityVerifyOptions.unverifiedPersonal`)
                  }}</span>
                  <span v-else-if="item.member_type === 'company'" class="error--text">{{
                    $t(`identityVerifyOptions.unverifiedCompany`)
                  }}</span>
                  <v-btn
                    v-if="id !== null"
                    depressed
                    class="my-2 ml-2"
                    elevation="0"
                    color="primary"
                    type="button"
                    small
                    outlined
                    @click="item.updateVerified = true"
                    :disabled="$store.getters.isLoading"
                    >{{ $t('update') }}</v-btn
                  >
                </div>
                <div class="d-flex align-center justify-space-between" v-else>
                  <div style="width: 115px; max-width: 115px" class="mr-2">
                    <form-select :fieldValue.sync="item.update_verified" :options="verifiedOptions" dense hideDetails />
                  </div>
                  <v-btn
                    depressed
                    class="my-2"
                    elevation="0"
                    color="primary"
                    type="button"
                    small
                    outlined
                    @click="saveMemberVerified(item.id)"
                    :disabled="$store.getters.isLoading"
                    :loading="$store.getters.isLoading"
                    >{{ $t('save') }}</v-btn
                  >
                </div>
              </template>
              <template v-slot:[`item.id_verify_method`]="{ item }">
                <span v-if="item.id_verify_method">{{
                  $t(`identityVerifyMethodOptions.${item.id_verify_method}`)
                }}</span>
              </template>
              <template v-slot:[`item.br_file`]="{ item }">
                <v-btn v-if="item.br_file" :href="`${$mediaPath}${item.br_file}`" target="_blank" text>{{
                  item.br_file
                }}</v-btn>
              </template>
              <template v-slot:[`item.id_file`]="{ item }">
                <v-btn v-if="item.id_file" :href="`${$mediaPath}${item.id_file}`" target="_blank" text>{{
                  item.id_file
                }}</v-btn>
              </template>
              <template v-slot:[`item.address_file`]="{ item }">
                <v-btn v-if="item.address_file" :href="`${$mediaPath}${item.address_file}`" target="_blank" text>{{
                  item.address_file
                }}</v-btn>
              </template>
            </datatable>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 公司秘書 -->
    <v-card class="mb-8" v-if="formData.type !== 'unlimit' && id !== null">
      <v-card-text>
        <v-row class="ma-0">
          <v-col cols="12">
            <h4 class="section-title">{{ $t('planTypeOption.secretary') }}</h4>
          </v-col>
          <v-col cols="12">
            <datatable
              :tableLoading="secretaryTableLoading"
              :tableHeaders="serviceTableHeaders"
              :tableData="secretaryTableData"
              disableToolbar
              disablePagination
              disableShadow
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 虛擬辦公室 -->
    <v-card class="mb-8" v-if="id !== null">
      <v-card-text>
        <v-row class="ma-0">
          <v-col cols="12">
            <h4 class="section-title">{{ $t('planTypeOption.virtual_office') }}</h4>
          </v-col>
          <v-col cols="12">
            <datatable
              :tableLoading="vrOfficeTableLoading"
              :tableHeaders="serviceTableHeaders"
              :tableData="vrOfficeTableData"
              disableToolbar
              disablePagination
              disableShadow
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 重要控制人登記冊 -->
    <v-card class="mb-8" v-if="id !== null">
      <v-card-text>
        <v-row class="ma-0">
          <v-col cols="12">
            <h4 class="section-title">{{ $t('addonServiceOptions.scr') }}</h4>
          </v-col>
          <v-col cols="12">
            <datatable
              :tableLoading="scrTableLoading"
              :tableHeaders="serviceTableHeaders"
              :tableData="scrTableData"
              disableToolbar
              disablePagination
              disableShadow
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 新增公司 Submit button -->
    <v-btn
      v-if="uid !== null"
      depressed
      elevation="0"
      class="ml-2 mb-4"
      color="primary"
      type="button"
      @click="createCompany()"
      :loading="$store.getters.isLoading"
      >{{ $t('save') }}</v-btn
    >

    <!-- Modal: 更新有效期Form -->
    <form-dialog
      ref="updateDocValidityDialog"
      formRef="updateDocValidityForm"
      title="updateValidityDate"
      @submit-clicked="submitDocValidDate()"
    >
      <v-row class="ma-0">
        <v-col cols="12">
          <form-date-picker
            label="validityDate"
            :fieldValue.sync="updateDocValidDate"
            disabledActionButtons
            manualInput
          />
        </v-col>
      </v-row>
    </form-dialog>

    <!-- Modal: 新增/更新公司成員Form -->
    <form-dialog
      ref="addMemberDialog"
      formRef="addMemberForm"
      :title="memberActionType === 'edit' ? 'updateMember' : 'addMember'"
      :maxWidth="1200"
      @submit-clicked="handleMemberFormSubmit()"
    >
      <v-row class="ma-0">
        <v-col cols="12" sm="6" lg="4">
          <form-select
            label="memberType"
            :fieldValue.sync="addMemberData.member_type"
            :options="formData.type === 'unlimit' ? unlimitedMemberTypeOptions : memberTypeOptions"
            required
            @changed="handleMemberTypeChanged()"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" v-if="formData.type === 'limited'">
          <form-select label="identity" :fieldValue.sync="addMemberData.identity" :options="identityOptions" required />
        </v-col>
      </v-row>
      <v-row
        class="ma-0"
        v-if="
          formData.type === 'limited' && (addMemberData.identity === 'shareholder' || addMemberData.identity === 'both')
        "
      >
        <v-col cols="12" sm="6" lg="4">
          <form-input label="equity" :fieldValue.sync="addMemberData.hold_stock" required type="number" />
        </v-col>
        <v-col cols="12" sm="6" lg="4">
          <form-input
            label="equityPercentage"
            :fieldValue.sync="addMemberData.percentage"
            required
            type="number"
            suffix="%"
          />
        </v-col>
      </v-row>
      <v-row class="ma-0" v-if="addMemberData.member_type">
        <template v-if="addMemberData.member_type === 'personal'">
          <v-col cols="12" sm="6" lg="4" v-if="formData.type !== 'limited'">
            <form-select label="title" :fieldValue.sync="addMemberData.title" :options="titleOptions" />
          </v-col>
          <v-col cols="12" sm="6" lg="4">
            <form-input label="fullNameZh" :fieldValue.sync="addMemberData.name_zh" />
          </v-col>
          <v-col cols="12" sm="6" lg="4" class="pa-0">
            <v-row class="ma-0 pa-0">
              <v-col cols="4" class="pr-0">
                <form-input label="lastnameEn" :fieldValue.sync="addMemberData.last_name" required />
              </v-col>
              <v-col cols="8">
                <form-input label="firstnameEn" :fieldValue.sync="addMemberData.first_name" required />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="6" lg="4" v-if="formData.type === 'limited'">
            <form-select label="gender" :fieldValue.sync="addMemberData.gender" :options="$genderOptions" />
          </v-col>
          <v-col cols="12" sm="6" lg="4" v-if="formData.type === 'limited'">
            <form-select label="isPep" :fieldValue.sync="addMemberData.pep" :options="$yesNoOptions" />
          </v-col>
          <v-col cols="12" sm="6" lg="4" v-if="formData.type === 'limited'">
            <form-select label="isOwner" :fieldValue.sync="addMemberData.owner" :options="$yesNoOptions" />
          </v-col>
          <v-col cols="12" sm="6" lg="4">
            <form-date-picker
              label="birthday"
              :fieldValue.sync="addMemberData.birthday"
              disabledActionButtons
              manualInput
              disableFuture
              :maxDate="birthMax"
            />
          </v-col>
          <v-col cols="12" sm="6" lg="4" v-if="formData.type === 'limited'">
            <form-select label="idType" :fieldValue.sync="addMemberData.id_type" :options="idTypeOptions" />
          </v-col>
          <v-col cols="12" sm="6" lg="4">
            <form-input
              :label="formData.type === 'limited' ? 'idNumber' : 'hkidNumber'"
              :fieldValue.sync="addMemberData.id_number"
              :customRules="[v => $validate.isIdNumber(v, addMemberData.id_type, true)]"
            />
          </v-col>
          <v-col cols="12" sm="6" lg="4" v-if="formData.type === 'limited'">
            <form-select
              label="issueCountry"
              :fieldValue.sync="addMemberData.sign_country"
              :options="$countryOptions"
            />
          </v-col>
          <v-col cols="12" sm="6" lg="4">
            <form-select
              label="identityVerifyMethod"
              :fieldValue.sync="addMemberData.id_verify_method"
              :options="idVerifyMethodOptions"
            />
          </v-col>
          <v-col cols="12" sm="6" lg="4">
            <form-select
              label="identityVerify"
              :fieldValue.sync="addMemberData.verified"
              :options="verifiedOptions"
              required
            />
          </v-col>
          <v-col cols="12">
            <image-uploader
              inputRef="uploadId"
              label="uploadIdFile"
              :fileValue.sync="addMemberData.id_file"
              fileType="imgWithPDF"
            />
          </v-col>
        </template>
        <template v-if="addMemberData.member_type === 'company'">
          <v-col cols="12" sm="6" lg="4">
            <form-input label="companyNameZh" :fieldValue.sync="addMemberData.company_name_zh" />
          </v-col>
          <v-col cols="12" sm="6" lg="4">
            <form-input label="companyNameEn" :fieldValue.sync="addMemberData.company_name_en" required />
          </v-col>
          <v-col cols="12" sm="6" lg="4" v-if="formData.type !== 'limited'">
            <form-select
              label="regCountry"
              :fieldValue.sync="addMemberData.reg_country"
              :options="$regCountryOptions"
            />
          </v-col>
          <v-col cols="12" sm="6" lg="4" class="pa-0">
            <v-row class="ma-0 pa-0">
              <v-col cols="8" class="pr-0">
                <form-input
                  label="brNumber"
                  :fieldValue.sync="addMemberData.br_number_prefix"
                  type="number"
                  isIntegerOnly
                  :customRules="[v => !v || $validate.regex('^[0-9]{8}$', v) || $t('form.brNumberInv')]"
                  @input-value="handleBrPrefixInput($event)"
                  @onkeypress="handleBrPrefixKeypress($event)"
                >
                  <template slot="append-outer">-</template>
                </form-input>
              </v-col>
              <v-col cols="4">
                <form-input
                  :fieldValue.sync="addMemberData.br_number_suffix"
                  :required="addMemberData.br_number_prefix != ''"
                  type="number"
                  isIntegerOnly
                  :customRules="[v => !v || $validate.regex('^[0-9]{3}$', v) || $t('form.brNumberInv')]"
                  inputRef="brNumSuffixInput"
                  ref="brNumSuffix"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="6" lg="4">
            <form-select
              label="identityVerify"
              :fieldValue.sync="addMemberData.verified"
              :options="verifiedOptions"
              required
            />
          </v-col>
          <v-col cols="12">
            <image-uploader
              inputRef="uploadBr"
              label="uploadBrFile"
              :fileValue.sync="addMemberData.br_file"
              fileType="imgWithPDF"
            />
          </v-col>
        </template>

        <v-col cols="12" sm="6" lg="4" class="pa-0">
          <v-row class="ma-0 pa-0">
            <v-col cols="4" class="pr-0">
              <form-select :fieldValue.sync="addMemberData.contact_zone" :options="$phoneCodeOptions" />
            </v-col>
            <v-col cols="8">
              <form-input
                label="contactMobile"
                :fieldValue.sync="addMemberData.contact_tel"
                type="tel"
                :customRules="[v => !v || $validate.isMobile(v, addMemberData.contact_zone, false)]"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" sm="6" lg="4">
          <form-input label="email" :fieldValue.sync="addMemberData.email" type="email" />
        </v-col>

        <template v-if="addMemberData.member_type === 'personal' && formData.type === 'limited'">
          <v-col cols="12">
            <form-input
              label="homeAddress"
              :fieldValue.sync="addMemberData.home_address"
              @input-value="handleHomeAddressInput($event)"
            />
          </v-col>
          <v-col cols="12">
            <v-checkbox
              v-model="addMemberData.same_address"
              class="custom-checkbox sm"
              :label="$t('sameAsHomeAddress')"
              @change="handleSameAddressChanged($event)"
            ></v-checkbox>
            <form-input
              label="contactAddress"
              :fieldValue.sync="addMemberData.mail_address"
              :disabled="addMemberData.same_address"
            />
          </v-col>
        </template>

        <template v-if="addMemberData.member_type === 'personal' && formData.type !== 'limited'">
          <v-col cols="12">
            <form-input label="commonAddress" :fieldValue.sync="addMemberData.mail_address" />
          </v-col>
          <v-col cols="12">
            <image-uploader
              inputRef="uploadAddress"
              label="uploadAddressFile"
              :fileValue.sync="addMemberData.address_file"
              fileType="imgWithPDF"
            />
          </v-col>
        </template>

        <v-col cols="12" v-if="addMemberData.member_type === 'company'">
          <form-input label="regAddress" :fieldValue.sync="addMemberData.reg_address" />
        </v-col>
      </v-row>
    </form-dialog>

    <!-- Modal: 新增服務Form -->
    <form-dialog
      ref="addServiceDialog"
      formRef="addServiceForm"
      title="addService"
      :maxWidth="1200"
      @submit-clicked="submitAddService()"
    >
      <v-row class="ma-0">
        <v-col cols="12" sm="6" lg="4">
          <form-select
            label="selectService"
            :fieldValue.sync="addServiceData.type"
            :options="serviceOptions"
            required
            @changed="onAddServicePlanChanged()"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" v-if="addServiceData.type === 'secretary'">
          <form-select label="selectPlan" :fieldValue.sync="addServiceData.plan" :options="secretaryOptions" required />
        </v-col>
        <v-col cols="12" sm="6" lg="4" v-if="addServiceData.type === 'virtual_office'">
          <form-select label="selectPlan" :fieldValue.sync="addServiceData.plan" :options="vrOfficeOptions" required />
        </v-col>
      </v-row>
      <v-row class="ma-0" v-if="addServiceData.type === 'update_corporate_info'">
        <v-col cols="12" md="6" lg="3" v-for="item in updateInfoPlans" :key="item.id">
          <v-checkbox multiple v-model="addServiceData.update_info_selected" class="custom-checkbox" :value="item.id">
            <template v-slot:label>
              <div>
                {{ item.name_zh }}
                <span class="primary--text">{{ item.display_price_zh }}</span>
                <small class="d-block" v-if="item.subtitle_zh">{{ item.subtitle_zh }}</small>
              </div>
            </template>
          </v-checkbox>
        </v-col>
      </v-row>

      <v-row class="ma-0" v-if="addServiceData.type != ''">
        <v-col cols="12" sm="6" lg="4">
          <form-select
            label="paymentMethod"
            :fieldValue.sync="addServiceData.payment_method"
            :options="paymentMethodOptions"
            required
          />
        </v-col>
        <v-col cols="12">
          <image-uploader
            inputRef="uploadPayment"
            label="uploadPaymentFile"
            :fileValue.sync="addServiceData.payment_file"
            fileType="imgWithPDF"
          />
        </v-col>
      </v-row>
    </form-dialog>

    <!-- Upload file input -->
    <v-file-input
      v-show="false"
      v-model="docFileValue"
      ref="docUploadInput"
      :accept="$common.getAcceptFileFormat('imgWithPDF')"
      @change="onDocumentFilePicked($event)"
    />
    <confirm-dialog ref="confirmUploadDocDialog" title="confirmUpdateDoc" :isDeleteDialog="false">
      <v-btn plain depressed :ripple="false" class="pa-0" @click.prevent="viewDocumentFile(uploadedDocFile)">{{
        uploadedDocFile
      }}</v-btn>
    </confirm-dialog>

    <confirm-dialog ref="confirmRemove" />
  </v-container>
</template>

<script>
import FormSelect from '@/components/form/FormSelect.vue'
import Datatable from '@/components/Datatable.vue'
import FormInput from '@/components/form/FormInput.vue'
import FormDatePicker from '@/components/form/FormDatePicker.vue'
import FormDialog from '@/components/FormDialog.vue'
import ImageUploader from '@/components/form/ImageUploader.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
export default {
  name: 'CompanyDetail',
  components: {
    FormSelect,
    Datatable,
    FormInput,
    FormDatePicker,
    FormDialog,
    ImageUploader,
    ConfirmDialog,
  },
  computed: {
    id() {
      const id = parseInt(this.$route.params.id)
      if (!isNaN(id)) {
        return id
      }

      return null
    },
    uid() {
      const id = parseInt(this.$route.params.uid)
      if (!isNaN(id)) {
        return id
      }

      return null
    },
    totalSharePrice() {
      if (
        this.$validate.regexFloating(this.formData.total_share) &&
        this.$validate.regexFloating(this.formData.price_per)
      ) {
        return parseFloat(this.formData.total_share) * parseFloat(this.formData.price_per)
      }

      return ''
    },
    birthMax() {
      const max_date = new Date()
      max_date.setFullYear(max_date.getFullYear() - 18)
      return this.$Formatter.formatDate(max_date)
    },
    memberTableHeaders() {
      const vm = this
      if (this.formData.type === 'limited') {
        return [
          { text: vm.$t('memberType'), value: 'member_type', width: 150 },
          { text: vm.$t('identity'), value: 'identity', width: 130 },
          { text: vm.$t('equity'), value: 'hold_stock' },
          { text: vm.$t('equityPercentage'), value: 'percentage' },
          { text: vm.$t('fullNameZh'), value: 'name_zh' },
          { text: vm.$t('fullNameEn'), value: 'name_en', width: 150 },
          { text: vm.$t('gender'), value: 'gender' },
          { text: vm.$t('pep'), value: 'pep' },
          { text: vm.$t('owner'), value: 'owner' },
          { text: vm.$t('birthday'), value: 'birthday', width: 150 },
          { text: vm.$t('idType'), value: 'id_type', width: 130 },
          { text: vm.$t('idNumber'), value: 'id_number' },
          { text: vm.$t('issueCountry'), value: 'sign_country' },
          { text: vm.$t('companyNameEn'), value: 'company_name_en', width: 200 },
          { text: vm.$t('companyNameZh'), value: 'company_name_zh' },
          { text: vm.$t('brNumber'), value: 'br_number', width: 150 },
          { text: vm.$t('contactMobile'), value: 'phone', width: 150 },
          { text: vm.$t('email'), value: 'email' },
          { text: vm.$t('homeAddress'), value: 'home_address', width: 250 },
          { text: vm.$t('contactAddress'), value: 'mail_address', width: 250 },
          { text: vm.$t('regAddress'), value: 'reg_address', width: 250 },
          { text: vm.$t('identityVerify'), value: 'verified', width: 250 },
          { text: vm.$t('identityVerifyDate'), value: 'id_verify_date', width: 150 },
          { text: vm.$t('identityVerifyMethod'), value: 'id_verify_method' },
          { text: vm.$t('idFile'), value: 'id_file', align: 'center' },
          { text: vm.$t('brFile'), value: 'br_file', align: 'center' },
          { text: '', value: 'actionViewDelete', align: 'end' },
        ]
      } else {
        return [
          { text: vm.$t('memberType'), value: 'member_type', width: 150 },
          { text: vm.$t('title'), value: 'title', width: 80 },
          { text: vm.$t('fullNameZh'), value: 'name_zh' },
          { text: vm.$t('fullNameEn'), value: 'name_en', width: 150 },
          { text: vm.$t('birthday'), value: 'birthday', width: 150 },
          { text: vm.$t('hkidNumber'), value: 'id_number' },
          { text: vm.$t('commonAddress'), value: 'mail_address', width: 250 },
          { text: vm.$t('companyNameEn'), value: 'company_name_en', width: 200 },
          { text: vm.$t('companyNameZh'), value: 'company_name_zh' },
          { text: vm.$t('brNumber'), value: 'br_number', width: 150 },
          { text: vm.$t('regCountry'), value: 'reg_country', width: 200 },
          { text: vm.$t('contactMobile'), value: 'phone', width: 200 },
          { text: vm.$t('email'), value: 'email' },
          { text: vm.$t('regAddress'), value: 'reg_address', width: 250 },
          { text: vm.$t('identityVerify'), value: 'verified', width: 250 },
          { text: vm.$t('identityVerifyDate'), value: 'id_verify_date', width: 150 },
          { text: vm.$t('identityVerifyMethod'), value: 'id_verify_method' },
          { text: vm.$t('idFile'), value: 'id_file', align: 'center' },
          { text: vm.$t('brFile'), value: 'br_file', align: 'center' },
          { text: vm.$t('addressFile'), value: 'address_file', align: 'center' },
          { text: '', value: 'actionViewDelete', align: 'end' },
        ]
      }
    },
    disableEdit() {
      return false
    },
    serviceOptions() {
      let list = []
      if (this.formData.type !== 'unlimit') {
        list.push({ text: this.$t('planTypeOption.secretary'), value: 'secretary' })
      }

      list = [
        ...list,
        { text: this.$t('planTypeOption.virtual_office'), value: 'virtual_office' },
        { text: this.$t('planTypeOption.update_corporate_info'), value: 'update_corporate_info' },
        { text: this.$t('planTypeOption.deregistration'), value: 'deregistration' },
        { text: this.$t('updateBr'), value: 'br' },
        { text: this.$t('addonServiceOptions.scr'), value: 'scr' },
        { text: this.$t('planTypeOption.stamp'), value: 'stamp' },
      ]
      return list
    },
  },
  data: vm => ({
    rowCount: 0,
    userData: null,
    companyData: null,
    formData: {
      user_id: -1,
      type: '',
      name_en: '',
      name_zh: '',
      contact_zh: '',
      contact_en: '',
      contact_zone: '852',
      contact_tel: '',
      gender: '',
      contact_email: '',
      mail_address: '',
      register_address_type: '',
      register_address: '',

      fund_source: '',
      fund_source_other: '',
      fund_source_country: '',
      business_description: '',
      main_product_service: '',
      main_business_location: '',
      expected_annual_income: '',
      total_share: '',
      price_per: '',
      company_start_at: '',
      company_status: 'active',
      br_number_prefix: '',
      br_number_suffix: '',
      acco_number: '',
      br_file: '',
      acco_file: '',
      business_start_date: '',
      business_type: [],
      sale_string: '',
      made_string: '',
      other_string: '',
    },

    // ---- 新增服務
    addServiceData: {
      type: '',
      plan: -1,
      payment_method: '',
      payment_file: '',
      update_info_selected: [],
    },
    allPlansData: [],
    secretaryOptions: [],
    vrOfficeOptions: [],
    updateInfoPlans: [],

    // ---- 公司文件
    companyDocTableLoading: true,
    companyDocTableData: [],

    updateDocValidDate: '',

    docFileValue: null,
    uploadedDocFile: '',
    uploadDocType: '',

    // ---- 其他公司文件
    otherDocTableLoading: true,
    otherDocTableData: [],

    // ---- 公司成員
    memberTableData: [],
    addMemberData: {
      member_type: '',
      identity: '', // For "limited" only
      contact_zone: '852',
      contact_tel: '',
      email: '',
      hold_stock: '', // For "limited" only
      percentage: '', // For "limited" only
      verified: false,

      // -- personal
      title: '',
      name_zh: '',
      first_name: '',
      last_name: '',
      gender: '', // For "limited" only
      pep: '', // For "limited" only
      owner: '', // For "limited" only
      birthday: '',
      id_type: '', // For "limited" only
      id_number: '',
      sign_country: '', // For "limited" only
      id_file: '',
      id_verify_method: '',
      home_address: '', // For "limited" only
      mail_address: '',
      same_address: false, // For "limited" only
      address_file: '',

      // -- company
      company_name_zh: '',
      company_name_en: '',
      br_number_prefix: '',
      br_number_suffix: '000',
      br_file: '',
      reg_address: '',
      reg_country: '',
    },
    memberActionType: '',

    // ---- 公司秘書
    secretaryTableLoading: true,
    secretaryTableData: [],

    // ---- 虛擬辦公室
    vrOfficeTableLoading: true,
    vrOfficeTableData: [],

    // ---- 重要控制人登記冊
    scrTableLoading: true,
    scrTableData: [],

    companyDocTableHeaders: [
      { text: vm.$t('companyDoc'), value: 'doc_name', width: 200 },
      { text: vm.$t('validityDate'), value: 'validity_date', width: 130 },
      { text: vm.$t('completeDate'), value: 'complete_date', width: 130 },
      { text: vm.$t('status'), value: 'item_status', width: 220 },
      { text: '', value: 'action', align: 'end', width: 320 },
    ],
    otherDocTableHeaders: [
      { text: vm.$t('uploadDate'), value: 'create_date' },
      { text: '', value: 'actionViewFile', align: 'end' },
    ],
    serviceTableHeaders: [
      { text: vm.$t('servicePlan'), value: 'plan' },
      { text: vm.$t('expiredDate'), value: 'expired_date' },
    ],
    companyDocStatusOptions: [
      { value: 'valid', text: vm.$t('docStatusOption.valid') },
      { value: 'expiring_soon', text: vm.$t('docStatusOption.expiring_soon') },
    ],
    companyStatusOptions: [
      { value: 'active', text: vm.$t('companyStatusOption.active') },
      { value: 'quit', text: vm.$t('companyStatusOption.quit') },
    ],
    memberTypeOptions: [
      { text: vm.$t('memberTypeOptions.personal'), value: 'personal' },
      { text: vm.$t('memberTypeOptions.company'), value: 'company' },
    ],
    unlimitedMemberTypeOptions: [
      { text: vm.$t('unlimitedMemberTypeOptions.personal'), value: 'personal' },
      { text: vm.$t('unlimitedMemberTypeOptions.company'), value: 'company' },
    ],
    identityOptions: [
      { text: vm.$t('identityOptions.director'), value: 'director' },
      { text: vm.$t('identityOptions.shareholder'), value: 'shareholder' },
      { text: vm.$t('identityOptions.both'), value: 'both' },
    ],
    idTypeOptions: [
      { text: vm.$t('idTypeOptions.hkid'), value: 'hkid' },
      { text: vm.$t('idTypeOptions.passport'), value: 'passport' },
    ],
    idVerifyMethodOptions: [
      { text: vm.$t('identityVerifyMethodOptions.whatsapp'), value: 'whatsapp' },
      { text: vm.$t('identityVerifyMethodOptions.zoom'), value: 'zoom' },
      { text: vm.$t('identityVerifyMethodOptions.wechat'), value: 'wechat' },
    ],
    verifiedOptions: [
      { text: vm.$t('identityVerifyOptions.unverified'), value: false },
      { text: vm.$t('identityVerifyOptions.verified'), value: true },
    ],
    titleOptions: [
      { text: vm.$t('titleOptions.mr'), value: 'mr' },
      { text: vm.$t('titleOptions.miss'), value: 'miss' },
      { text: vm.$t('titleOptions.mrs'), value: 'mrs' },
      { text: vm.$t('titleOptions.ms'), value: 'ms' },
      { text: vm.$t('titleOptions.doctor'), value: 'doctor' },
      { text: vm.$t('titleOptions.professor'), value: 'professor' },
    ],
    paymentMethodOptions: [
      { text: vm.$t('paymentMethodOptions.transfer'), value: 'transfer' },
      { text: vm.$t('paymentMethodOptions.credit_card'), value: 'offline_credit_card' },
    ],
    fundSourceOptions: [
      { text: vm.$t('fundSourceOptions.business_income_and_profit'), value: 'business_income_and_profit' },
      { text: vm.$t('fundSourceOptions.inheritance'), value: 'inheritance' },
      { text: vm.$t('fundSourceOptions.intra_group_financing'), value: 'intra_group_financing' },
      {
        text: vm.$t('fundSourceOptions.investment_by_beneficial_owner_partner'),
        value: 'investment_by_beneficial_owner_partner',
      },
      { text: vm.$t('fundSourceOptions.loan'), value: 'loan' },
      { text: vm.$t('fundSourceOptions.personal_savings'), value: 'personal_savings' },
      { text: vm.$t('fundSourceOptions.sale_of_property_asset'), value: 'sale_of_property_asset' },
      { text: vm.$t('fundSourceOptions.others'), value: 'others' },
    ],
    turnoverOptions: [
      { text: vm.$t('turnoverOptions.lessThan'), value: '<HK$1,000,000' },
      { text: vm.$t('turnoverOptions.between'), value: 'HK$1,000,000-HK$10,000,000' },
      { text: vm.$t('turnoverOptions.moreThan'), value: '>HK$10,000,000' },
    ],
    regAddressTypeOptions: [
      { text: vm.$t('companyRegAddressTypeOption.custom'), value: 'custom' },
      { text: vm.$t('companyRegAddressTypeOption.corp'), value: 'corp' },
    ],
  }),
  methods: {
    async getCompany() {
      try {
        const data = await this.$Fetcher.GetCompanyById(this.id)
        this.companyData = data
        for (let key in this.formData) {
          if (this.$validate.DataValid(data[key])) {
            if (key === 'fund_source') {
              const found = this.fundSourceOptions.find(el => el.value === data[key])
              if (!found) {
                this.formData.fund_source = 'others'
                this.formData.fund_source_other = data[key]
              } else {
                this.formData[key] = data[key]
              }
            } else {
              this.formData[key] = data[key]
            }
          }
        }

        if (data.br_number) {
          const split = data.br_number.split('-')
          this.formData.br_number_prefix = split[0]
          this.formData.br_number_suffix = split[1]
        }

        // --- render 公司文件
        let doc = ['br', 'nar1', 'scr', 'duty', 'ci', 'nnc', 'upload_br']
        if (this.formData.type === 'unlimit') {
          doc = ['br', 'scr', 'duty']
        }
        this.companyDocTableData = doc.map(key => this.parseDocument(key))

        // --- render 公司成員
        this.memberTableData = data.member_list.map(this.parseMember)
        this.$store.dispatch('setInitLoading', false)

        await this.getCompanyUser(data.user_id)
        await this.getCompanyDocument()
        await this.getCompanyOtherDocuments()
        await this.getCompanyServices()
      } catch (err) {
        this.$common.error(err)
        this.$store.dispatch('setAlert', {
          message: 'noData',
          redirect: 'UserList',
        })
        this.$store.dispatch('setInitLoading', false)
      }
    },
    async getCompanyUser(uid) {
      try {
        const data = await this.$Fetcher.GetUserById(uid)
        this.userData = data
      } catch (err) {
        this.$common.error(err)
      }
    },
    async getCompanyDocument() {
      this.companyDocTableLoading = true
      try {
        const data = await this.$Fetcher.GetCompanyDocument(this.id)
        for (let i = 0; i < this.companyDocTableData.length; i++) {
          this.$set(
            this.companyDocTableData,
            i,
            this.parseDocument(this.companyDocTableData[i].type, data[this.companyDocTableData[i].type]),
          )
        }
      } catch (err) {
        this.$common.error(err)
        for (let i = 0; i < this.companyDocTableData.length; i++) {
          this.$set(this.companyDocTableData, i, this.parseDocument(this.companyDocTableData[i].type))
        }
      } finally {
        this.companyDocTableLoading = false
      }
    },
    async getCompanyOtherDocuments() {
      this.otherDocTableLoading = true
      try {
        const { data } = await this.$Fetcher.GetCompanyDocumentsByType(this.id, 'order')
        this.otherDocTableData = data
      } catch (err) {
        this.$common.error(err)
        this.otherDocTableData = []
      } finally {
        this.otherDocTableLoading = false
      }
    },
    async getCompanyServices() {
      this.secretaryTableLoading = true
      this.vrOfficeTableLoading = true
      this.scrTableLoading = true
      try {
        const { data } = await this.$Fetcher.GetOrders({
          filters: [{ key: 'company_id', value: this.id }],
        })

        this.secretaryTableData = []
        this.vrOfficeTableData = []
        this.scrTableData = []

        data.forEach(item => {
          item.plan_data_list.forEach(plan => {
            // --- 公司秘書
            if (plan.type === 'secretary') {
              this.secretaryTableData.push({
                plan: plan.name_zh,
                expired_date: item.end_date.substring(0, 10),
                expiry: item.end_date,
              })
            } else if (plan.secretary === -1) {
              this.secretaryTableData.push({
                plan: `${this.$t(`planTypeOption.${plan.type}`)} - ${plan.name_zh}`,
                expired_date: item.end_date.substring(0, 10),
                expiry: item.end_date,
              })
            }

            // --- 虛擬辦公室
            if (plan.type === 'virtual_office') {
              this.vrOfficeTableData.push({
                plan: plan.name_zh,
                expired_date: item.end_date.substring(0, 10),
                expiry: item.end_date,
              })
            } else if (plan.company_address === -1) {
              this.vrOfficeTableData.push({
                plan: `${this.$t(`planTypeOption.${plan.type}`)} - ${plan.name_zh}`,
                expired_date: item.end_date.substring(0, 10),
                expiry: item.end_date,
              })
            }

            // --- 重要控制人登記冊
            if (plan.type === 'scr') {
              this.scrTableData.push({
                plan: plan.name_zh,
                expired_date: item.end_date.substring(0, 10),
                expiry: item.end_date,
              })
            } else if (plan.scr === -1) {
              this.scrTableData.push({
                plan: `${this.$t(`planTypeOption.${plan.type}`)} - ${plan.name_zh}`,
                expired_date: item.end_date.substring(0, 10),
                expiry: item.end_date,
              })
            }
          })

          item.addon_data_list.forEach(addon => {
            // --- 重要控制人登記冊
            if (addon.type === 'scr') {
              this.scrTableData.push({
                plan: `${this.$t('addon')} - ${addon.name_zh}`,
                expired_date: item.end_date.substring(0, 10),
                expiry: item.end_date,
              })
            }
          })
        })

        this.secretaryTableData.sort((a, b) => new Date(b.expiry).getTime() - new Date(a.expiry).getTime())
        this.vrOfficeTableData.sort((a, b) => new Date(b.expiry).getTime() - new Date(a.expiry).getTime())
        this.scrTableData.sort((a, b) => new Date(b.expiry).getTime() - new Date(a.expiry).getTime())
      } catch (err) {
        this.$common.error(err)
        this.secretaryTableData = []
        this.vrOfficeTableData = []
        this.scrTableData = []
      } finally {
        this.secretaryTableLoading = false
        this.vrOfficeTableLoading = false
        this.scrTableLoading = false
      }
    },

    getCompanyPayload() {
      let payload = {
        name_zh: this.formData.name_zh,
        name_en: this.formData.name_en,
        contact_zh: this.formData.contact_zh,
        contact_en: this.formData.contact_en,
        contact_zone: this.formData.contact_zone,
        contact_tel: this.formData.contact_tel,
        gender: this.formData.gender,
        contact_email: this.formData.contact_email,
        mail_address: this.formData.mail_address,

        fund_source:
          this.formData.type === 'limited'
            ? this.formData.fund_source === 'others'
              ? this.formData.fund_source_other
              : this.formData.fund_source
            : '',
        fund_source_country: this.formData.type === 'limited' ? this.formData.fund_source_country : '',
        business_description: this.formData.type === 'limited' ? this.formData.business_description : '',
        main_product_service: this.formData.type === 'limited' ? this.formData.main_product_service : '',
        main_business_location: this.formData.type === 'limited' ? this.formData.main_business_location : '',
        expected_annual_income: this.formData.type === 'limited' ? this.formData.expected_annual_income : '',
        total_share: this.formData.type === 'limited' ? this.formData.total_share : '',
        price_per: this.formData.type === 'limited' ? this.formData.price_per : '',

        business_type: this.formData.type === 'unlimit' ? this.formData.business_type : [],
        sale_string:
          this.formData.type === 'unlimit' && this.formData.business_type.includes('wholesale')
            ? this.formData.sale_string
            : '',
        made_string:
          this.formData.type === 'unlimit' && this.formData.business_type.includes('manufacture')
            ? this.formData.made_string
            : '',
        other_string:
          this.formData.type === 'unlimit' && this.formData.business_type.includes('other')
            ? this.formData.other_string
            : '',
        business_start_date: this.formData.type === 'unlimit' ? this.formData.business_start_date : '',

        br_number:
          this.formData.type !== 'group_assoc' && this.formData.br_number_prefix
            ? `${this.formData.br_number_prefix}-${this.formData.br_number_suffix}`
            : '',
        br_file: this.formData.type !== 'group_assoc' ? this.formData.br_file : '',
        have_br: this.formData.type !== 'group_assoc' ? this.formData.have_br : false,

        acco_number: this.formData.type === 'group_assoc' ? this.formData.acco_number : '',
        acco_file: this.formData.type === 'group_assoc' ? this.formData.acco_file : '',

        register_address_type: this.formData.type !== 'group_assoc' ? this.formData.register_address_type : '',
        register_address: this.formData.type !== 'group_assoc' ? this.formData.register_address : '',

        company_status: this.formData.company_status,
        company_start_at: this.formData.type !== 'group_assoc' ? this.formData.company_start_at : '',
      }

      if (this.uid !== null) {
        payload['type'] = this.formData.type
        payload['user_list'] = []
        payload['known_from'] = this.userData?.known_from || []
        payload['have_br'] = false
        payload['member_list'] = this.memberTableData.map(this.getMemberPayload)
      }

      return payload
    },

    // --- 新增公司
    async createCompany() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      const valid = await this.$refs['basicForm'].validate()
      if (!valid) {
        this.$store.dispatch('toggleSnack', {
          message: 'required',
        })
        return
      }

      this.$store.dispatch('setLoading', true)
      const payload = this.getCompanyPayload()
      try {
        await this.$Fetcher.NewCompany(this.uid, payload)
        this.$store.dispatch('setLoading', false)
        this.$store.dispatch('setDataIsUpdated', false)
        this.$store.dispatch('toggleSnack', {
          type: 'success',
          message: 'message.saveSuccess',
          redirect: { name: 'UserDetail', params: { id: this.uid } },
        })
      } catch (err) {
        this.$common.error(err)
        this.$store.dispatch('setLoading', false)
        this.$store.dispatch('setAlert', {
          title: 'message.saveFail',
          message: err,
          type: 'error',
        })
      }
    },

    // --- 更新公司
    async saveCompany() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      const valid = await this.$refs['basicForm'].validate()
      if (!valid) {
        this.$store.dispatch('toggleSnack', {
          message: 'required',
        })
        return
      }

      this.$store.dispatch('setLoading', true)

      const payload = this.getCompanyPayload()
      try {
        const data = await this.$Fetcher.UpdateCompany(this.id, payload)
        this.companyData = data
        this.$store.dispatch('setDataIsUpdated', false)
        this.$store.dispatch('setLoading', false)
        this.$store.dispatch('toggleSnack', {
          type: 'success',
          message: 'message.saveSuccess',
          refresh: true,
        })
      } catch (err) {
        this.$common.error(err)
        this.$store.dispatch('setLoading', false)
        this.$store.dispatch('setAlert', {
          title: 'message.saveFail',
          message: err,
          type: 'error',
        })
      }
    },

    // --- 刪除公司
    async deleteCompany() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }
      const confirm = await this.$refs['confirmRemove'].show()
      if (confirm) {
        this.$store.dispatch('setLoading', true)
        try {
          await this.$Fetcher.DeleteCompany(this.id)
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('setDataIsUpdated', false)
          this.$store.dispatch('toggleSnack', {
            type: 'success',
            message: 'message.saveSuccess',
            redirect: { name: 'UserDetail', params: { id: this.userData?.id } },
          })
        } catch (err) {
          this.$common.error(err)
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('setAlert', {
            title: 'message.saveFail',
            message: err,
            type: 'error',
          })
        }
      }
    },

    handleCompanyBrPrefixInput(value) {
      const cleaned = value.substring(0, 8)
      this.formData.br_number_prefix = cleaned
      if (cleaned.length >= 8) {
        this.$refs.companyBrNumSuffix.$refs.companyBrNumSuffixInput.$el.querySelector('input').focus()
        if (this.formData.br_number_suffix) {
          this.$refs.companyBrNumSuffix.$refs.companyBrNumSuffixInput.$el.querySelector('input').select()
        }
      }
    },
    handleCompanyBrPrefixKeypress(ev) {
      if (this.formData.br_number_prefix.length >= 8) {
        ev.preventDefault()
        return
      }
    },
    handleRegAddressTypeChanged() {
      if (this.formData.register_address_type === 'corp') {
        this.formData.register_address = this.$vrAddress
      } else {
        this.formData.register_address = ''
      }
    },

    // ----------------- 公司文件 -----------------
    parseDocument(key, data = null) {
      const obj = {
        type: key,
        doc_name: this.$t(`companyDocOptions.${key}`),
        data_id: data ? data.id : -1,
        validity_date: '',
        complete_date: '',
        item_status: '',
        file: data ? data.file : '',
        updateStatus: false,
        update_status: '',
      }
      if (data) {
        if (key === 'duty') {
          obj.complete_date = data.expiry.substring(0, 10)
        } else {
          obj.validity_date = data.expiry.substring(0, 10)
        }

        obj.item_status = data.item_status
        if (
          key !== 'duty' &&
          data.item_status !== 'pending' &&
          data.expiry &&
          this.$validate.isDateLess(new Date(data.expiry), this.$Formatter.getToday())
        ) {
          obj.item_status = 'expired'
        }

        obj.update_status = obj.item_status
      }

      return obj
    },

    handleDocumentSubmit(type, payload) {
      return new Promise(async (resolve, reject) => {
        const foundPos = this.companyDocTableData.map(el => el.type).indexOf(type)
        if (foundPos > -1) {
          this.$store.dispatch('setLoading', true)

          if (this.companyDocTableData[foundPos].data_id > -1) {
            // edit
            try {
              const data = await this.$Fetcher.UpdateCompanyDocument(
                this.companyDocTableData[foundPos].data_id,
                payload,
              )
              this.$store.dispatch('setLoading', false)
              this.$store.dispatch('toggleSnack', {
                type: 'success',
                message: 'message.saveSuccess',
                refresh: false,
              })
              this.$set(this.companyDocTableData, foundPos, this.parseDocument(data.type, data))
              resolve(data)
            } catch (err) {
              this.$common.error(err)
              this.$store.dispatch('setLoading', false)
              this.$store.dispatch('setAlert', {
                title: 'message.saveFail',
                message: err,
                type: 'error',
              })
              reject(err)
            }
          } else {
            // create
            try {
              let new_payload = {
                type: type,
                file: payload.file || '',
                item_status: payload.item_status || '',
                company_id: this.id,
                user_id: this.formData.user_id,
                expiry: payload.expiry || '',
              }

              const data = await this.$Fetcher.NewCompanyDocument(new_payload)
              this.$store.dispatch('setLoading', false)
              this.$store.dispatch('toggleSnack', {
                type: 'success',
                message: 'message.saveSuccess',
                refresh: false,
              })
              this.$set(this.companyDocTableData, foundPos, this.parseDocument(data.type, data))
              resolve(data)
            } catch (err) {
              this.$common.error(err)
              this.$store.dispatch('setLoading', false)
              this.$store.dispatch('setAlert', {
                title: 'message.saveFail',
                message: err,
                type: 'error',
              })
              reject(err)
            }
          }
        } else {
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('setAlert', {
            title: 'message.saveFail',
            message: 'Document not found',
            type: 'error',
          })
          reject(null)
        }
      })
    },

    // ---- 查看文件
    viewDocumentFile(file) {
      if (file) {
        window.open(this.$mediaPath + file)
      }
    },

    // ---- 更新有效期
    async openUpdateValidDate(type) {
      await this.$refs.updateDocValidityDialog.reset()
      this.$refs.updateDocValidityDialog.setEditId(type)
      this.updateDocValidDate = ''
      this.$refs.updateDocValidityDialog.show()
    },
    async submitDocValidDate() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      const type = this.$refs.updateDocValidityDialog.getEditId()
      try {
        await this.handleDocumentSubmit(type, {
          expiry: this.updateDocValidDate,
        })
        this.$refs.updateDocValidityDialog.hide()
      } catch {
        this.$refs.updateDocValidityDialog.setLoading(false)
      }
    },

    // ---- 更新狀態
    async saveDocumentStatus(type) {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }
      try {
        const found = this.companyDocTableData.find(el => el.type === type)
        await this.handleDocumentSubmit(type, {
          item_status: found.update_status,
        })
      } catch {}
    },

    // ---- 上載文件
    triggerDocUpload(type) {
      if (this.$store.getters.isLoading || this.$store.getters.uploading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      this.uploadDocType = type
      this.$refs.docUploadInput.$refs.input.click()
    },
    async onDocumentFilePicked(file) {
      if (!file) {
        return
      }

      const acceptType = this.$common.getAcceptFileFormat('imgWithPDF')
      const check = await this.$validate.validateFileInput(file, acceptType)
      if (check !== true) {
        this.$store.dispatch('toggleSnack', {
          message: check,
          type: 'error',
        })
        return
      }

      this.$store.dispatch('setUploadLoading', true)
      const reader = new FileReader()
      reader.onload = async event => {
        const payload = {
          upload_data: event.target.result,
          upload_file_type: file.type.split('/').pop(),
        }

        this.$XHR
          .post(
            {
              upload_file: JSON.stringify(payload),
            },
            progress => {
              this.$store.dispatch('toggleSnack', {
                type: 'processing',
                message: this.$t('message.uploading', {
                  percentage: this.$common.formatUploadProgress(progress.loaded, progress.total),
                }),
                closeManual: true,
              })
            },
          )
          .then(async data => {
            this.$store.dispatch('setUploadLoading', false)
            this.uploadedDocFile = data
            const confirm = await this.$refs.confirmUploadDocDialog.show()
            if (confirm) {
              this.submitUploadedDoc()
            }
          })
          .catch(err => {
            this.$store.dispatch('setUploadLoading', false)
            this.$common.error(err)
            this.$store.dispatch('toggleSnack', {
              message: 'message.uploadFail',
              type: 'error',
            })
          })
      }
      reader.readAsDataURL(file)
    },
    async submitUploadedDoc() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      try {
        await this.handleDocumentSubmit(this.uploadDocType, {
          file: this.uploadedDocFile,
        })
        this.uploadedDocFile = ''
        this.uploadDocType = ''
      } catch {}
    },

    // ----------------- 公司成員 -----------------
    parseMember(item) {
      this.rowCount += 1
      return {
        id: this.rowCount,
        member_type: item.member_type,
        identity: item.identity,
        contact_zone: item.contact_zone,
        contact_tel: item.contact_tel,
        email: item.email,
        hold_stock: item.hold_stock,
        percentage: item.percentage,
        verified: item.id_verified,

        title: item.title,
        name_zh: item.name_zh,
        first_name: item.first_name,
        last_name: item.last_name,
        gender: item.gender,
        pep: item.pep,
        owner: item.owner,
        birthday: item.birthday,
        id_type: item.id_type,
        id_number: item.id_number,
        sign_country: item.sign_country,
        id_file: item.id_file,
        id_verify_method: item.id_verify_method,
        home_address: item.home_address,
        mail_address: item.mail_address,
        same_address: item.same_address,
        address_file: item.address_file,

        company_name_zh: item.company_name_zh,
        company_name_en: item.company_name_en,
        br_number: item.br_number,
        br_number_prefix: item.br_number ? item.br_number.split('-')[0] : '',
        br_number_suffix: item.br_number ? item.br_number.split('-').pop() : '',
        br_file: item.br_file,
        reg_address: item.reg_address,
        reg_country: item.reg_country,

        id_verify_date: item.id_verify_date,
        // --- for update 身份驗證 status
        update_verified: item.id_verified,
        updateVerified: false,
      }
    },

    getMemberPayload(item) {
      let payload = {
        company_type: this.formData.type,
        member_type: item.member_type,
        identity: this.formData.type === 'limited' ? item.identity : '',
        title: this.formData.type !== 'limited' ? item.title : '',
        name_zh: item.member_type == 'personal' ? item.name_zh : '',
        first_name: item.member_type == 'personal' ? item.first_name : '',
        last_name: item.member_type == 'personal' ? item.last_name : '',
        company_name_zh: item.member_type == 'company' ? item.company_name_zh : '',
        company_name_en: item.member_type == 'company' ? item.company_name_en : '',
        reg_address: item.member_type == 'company' ? item.reg_address : '',
        br_number:
          item.member_type == 'company' && item.br_number_prefix
            ? `${item.br_number_prefix}-${item.br_number_suffix}`
            : '',
        br_file: item.member_type == 'company' ? item.br_file : '',
        reg_country: this.formData.type !== 'limited' && item.member_type == 'company' ? item.reg_country : '',
        gender: this.formData.type === 'limited' && item.member_type == 'personal' ? item.gender : '',
        pep: this.formData.type === 'limited' && item.member_type == 'personal' ? item.pep : '',
        owner: this.formData.type === 'limited' && item.member_type == 'personal' ? item.owner : '',
        birthday: item.member_type == 'personal' ? item.birthday : '',
        id_type: this.formData.type === 'limited' && item.member_type == 'personal' ? item.id_type : '',
        id_number: item.member_type == 'personal' ? item.id_number : '',
        id_verify_method: item.member_type == 'personal' ? item.id_verify_method : '',
        id_verified: item.verified,
        id_verify_date: '',
        sign_country: this.formData.type === 'limited' && item.member_type == 'personal' ? item.sign_country : '',
        id_file: item.member_type == 'personal' ? item.id_file : '',
        contact_zone: item.contact_zone,
        contact_tel: item.contact_tel,
        email: item.email,
        home_address: this.formData.type === 'limited' && item.member_type == 'personal' ? item.home_address : '',
        same_address:
          this.formData.type === 'limited' && item.member_type == 'personal'
            ? item.same_address
              ? 'true'
              : 'false'
            : '',
        mail_address: item.member_type == 'personal' ? item.mail_address : '',
        address_file: this.formData.type !== 'limited' ? item.address_file : '',
        address_verify: false,
        hold_type: '',
        hold_stock: this.formData.type === 'limited' && item.hold_stock ? parseFloat(item.hold_stock) : 0,
        percentage: this.formData.type === 'limited' && item.percentage ? parseFloat(item.percentage) : 0,
      }

      if (this.id !== null && this.memberActionType === 'edit') {
        const rowId = this.$refs['addMemberDialog'].getEditId()
        const found = this.memberTableData.find(el => el.id === rowId)
        if (found) {
          payload['id_verify_date'] = found.id_verify_date
        }
      }

      if (payload['id_file'] && payload['id_verify_date'] == '') {
        payload['id_verify_date'] = this.$Formatter.formatDate(this.$Formatter.getToday())
      }

      return payload
    },

    // --- 新增/更新公司成員
    async openAddMemberForm(type = 'new', rowId = -1) {
      this.memberActionType = type

      // reset
      for (let key in this.addMemberData) {
        if (key === 'contact_zone') {
          this.addMemberData[key] = '852'
        } else if (key === 'same_address' || key === 'verified') {
          this.addMemberData[key] = false
        } else if (key === 'br_number_suffix') {
          this.addMemberData[key] = '000'
        } else {
          this.addMemberData[key] = ''
        }
      }

      if (this.formData.type !== 'limited') {
        this.addMemberData.id_type = 'hkid'
      }

      if (type === 'edit') {
        const found = this.memberTableData.find(el => el.id === rowId)
        if (found) {
          this.$refs['addMemberDialog'].setEditId(rowId)
          for (let key in this.addMemberData) {
            if (found[key]) {
              this.$set(this.addMemberData, key, found[key])
            }
          }
          this.$refs['addMemberDialog'].show()
        }
      } else {
        this.$refs['addMemberDialog'].show()
      }
    },
    handleSameAddressChanged(value) {
      if (value) {
        this.addMemberData.mail_address = this.addMemberData.home_address
      }
    },
    handleHomeAddressInput(value) {
      if (this.addMemberData.same_address) {
        this.addMemberData.mail_address = value
      }
    },
    handleBrPrefixInput(value) {
      const cleaned = value.substring(0, 8)
      this.addMemberData.br_number_prefix = cleaned
      if (cleaned.length >= 8) {
        this.$refs.brNumSuffix.$refs.brNumSuffixInput.$el.querySelector('input').focus()
        if (this.addMemberData.br_number_suffix) {
          this.$refs.brNumSuffix.$refs.brNumSuffixInput.$el.querySelector('input').select()
        }
      }
    },
    handleBrPrefixKeypress(ev) {
      if (this.addMemberData.br_number_prefix.length >= 8) {
        ev.preventDefault()
        return
      }
    },
    async handleMemberTypeChanged() {
      await this.$refs['addMemberDialog'].reset()
      for (let key in this.addMemberData) {
        if (key !== 'member_type') {
          if (key === 'contact_zone') {
            this.addMemberData[key] = '852'
          } else if (key === 'same_address' || key === 'verified') {
            this.addMemberData[key] = false
          } else if (key === 'br_number_suffix') {
            this.addMemberData[key] = '000'
          } else {
            this.addMemberData[key] = ''
          }
        }
      }

      if (this.formData.type !== 'limited') {
        this.addMemberData.id_type = 'hkid'
      }
    },

    handleMemberFormSubmit() {
      if (this.memberActionType === 'edit') {
        this.updateMember()
      } else {
        this.addMember()
      }
    },

    // --- 新增公司成員
    async addMember() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      const payload = this.getMemberPayload(this.addMemberData)

      if (this.id !== null) {
        this.$store.dispatch('setLoading', true)
        try {
          const data = await this.$Fetcher.AddMemberToCompany(this.id, payload)
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('toggleSnack', {
            type: 'success',
            message: 'message.saveSuccess',
            refresh: false,
          })

          this.memberTableData = data.map(this.parseMember)
          this.$refs['addMemberDialog'].hide()
        } catch (err) {
          this.$common.error(err)
          this.$refs['addMemberDialog'].setLoading(false)
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('setAlert', {
            title: 'message.saveFail',
            message: err,
            type: 'error',
          })
        }
      } else if (this.uid !== null) {
        this.memberTableData.push(this.parseMember(payload))
        this.$refs['addMemberDialog'].hide()
      }
    },

    // --- 刪除公司成員
    async deleteMember(rowId) {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }
      const confirm = await this.$refs['confirmRemove'].show()
      if (confirm) {
        const foundPos = this.memberTableData.map(el => el.id).indexOf(rowId)
        if (foundPos > -1) {
          if (this.id !== null) {
            this.$store.dispatch('setLoading', true)
            try {
              const data = await this.$Fetcher.RemoveCompanyMember(this.id, foundPos)
              this.$store.dispatch('setLoading', false)
              this.$store.dispatch('toggleSnack', {
                type: 'success',
                message: 'message.saveSuccess',
                refresh: false,
              })

              this.memberTableData = data.map(this.parseMember)
            } catch (err) {
              this.$common.error(err)
              this.$store.dispatch('setLoading', false)
              this.$store.dispatch('setAlert', {
                title: 'message.saveFail',
                message: err,
                type: 'error',
              })
            }
          } else if (this.uid !== null) {
            this.memberTableData.splice(foundPos, 1)
          }
        }
      }
    },

    // --- 更新公司成員
    async updateMember() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      const rowId = this.$refs['addMemberDialog'].getEditId()
      const foundPos = this.memberTableData.map(el => el.id).indexOf(rowId)
      if (foundPos > -1) {
        const payload = this.getMemberPayload(this.addMemberData)

        if (this.id !== null) {
          this.$store.dispatch('setLoading', true)

          try {
            const data = await this.$Fetcher.UpdateCompanyMember(this.id, foundPos, payload)
            this.$store.dispatch('setLoading', false)
            this.$store.dispatch('toggleSnack', {
              type: 'success',
              message: 'message.saveSuccess',
              refresh: false,
            })
            this.memberTableData = data.map(this.parseMember)
            this.$refs['addMemberDialog'].hide()
          } catch (err) {
            this.$common.error(err)
            this.$store.dispatch('setLoading', false)
            this.$store.dispatch('setAlert', {
              title: 'message.saveFail',
              message: err,
              type: 'error',
            })
          }
        } else if (this.uid !== null) {
          this.$set(this.memberTableData, foundPos, this.parseMember(payload))
          this.$refs['addMemberDialog'].hide()
        }
      }
    },

    // --- update 身份驗證 status (in table row)
    async saveMemberVerified(rowId) {
      if (this.id === null) {
        return
      }
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      const foundPos = this.memberTableData.map(el => el.id).indexOf(rowId)
      if (foundPos > -1) {
        this.$store.dispatch('setLoading', true)
        try {
          const data = await this.$Fetcher.UpdateCompanyMember(this.id, foundPos, {
            id_verified: this.memberTableData[foundPos].update_verified,
          })
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('toggleSnack', {
            type: 'success',
            message: 'message.saveSuccess',
            refresh: false,
          })
          this.memberTableData = data.map(this.parseMember)
        } catch (err) {
          this.$common.error(err)
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('setAlert', {
            title: 'message.saveFail',
            message: err,
            type: 'error',
          })
        }
      }
    },

    // ----------------- 新增服務 -----------------
    async openAddServiceForm() {
      if (this.$store.getters.isLoading) {
        return
      }
      await this.$refs['addServiceDialog'].reset()
      for (let key in this.addServiceData) {
        if (key === 'type' || key === 'payment_method' || key === 'payment_file') {
          this.addServiceData[key] = ''
        } else if (key === 'plan') {
          this.addServiceData[key] = -1
        } else {
          this.addServiceData[key] = false
        }
      }

      this.$store.dispatch('setLoading', true)
      if (this.allPlansData.length < 1) {
        await this.getPlans()
      }
      this.$store.dispatch('setLoading', false)
      this.$refs.addServiceDialog.show()
    },
    async getPlans() {
      try {
        const { data } = await this.$Fetcher.GetPlans({
          filters: [{ key: 'status', value: 'active' }],
          sort_dir: 'asc',
          order_by: 'price',
        })

        this.allPlansData = data

        const secretaryPlans = data.filter(el => el.type === 'secretary')
        this.secretaryOptions = secretaryPlans.map(item => {
          return { value: item.id, text: `${item.name_zh} ${item.name_en}` }
        })

        const vrOfficePlans = data.filter(el => el.type === 'virtual_office')
        this.vrOfficeOptions = vrOfficePlans.map(item => {
          return { value: item.id, text: `${item.name_zh} ${item.name_en}` }
        })

        const _updateInfoPlans = data.filter(el => el.type === 'update_corporate_info')
        if (this.formData.type === '' || this.formData.type === 'group_assoc') {
          this.updateInfoPlans = _updateInfoPlans
        } else {
          this.updateInfoPlans = _updateInfoPlans.filter(
            el => el.company_type === '' || el.company_type === this.formData.type,
          )
        }
      } catch {}
    },
    onAddServicePlanChanged() {
      this.$set(this.addServiceData, 'plan', -1)
      this.$set(this.addServiceData, 'update_info_selected', [])
    },

    async submitAddService() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      if (this.addServiceData.type === 'update_corporate_info' && this.addServiceData.update_info_selected.length < 1) {
        this.$store.dispatch('toggleSnack', {
          type: 'error',
          message: 'message.updateInfoOptionEmpty',
        })
        return
      }

      this.$store.dispatch('setLoading', true)
      let payload = {
        order_type: 'new',
        plan_id_list: [],
        company_id: this.id,
        addon_list: [],
        subtotal: 0,
        user_id: this.formData.user_id,
        payment_method: this.addServiceData.payment_method,
        payment_data: this.addServiceData.payment_file,
      }

      if (this.addServiceData.type === 'secretary' || this.addServiceData.type === 'virtual_office') {
        const foundPlan = this.allPlansData.find(el => el.id === this.addServiceData.plan)
        if (!foundPlan) {
          this.$store.dispatch('setLoading', false)
          this.$refs['addServiceDialog'].setLoading(false)
          this.$store.dispatch('setAlert', {
            title: 'message.saveFail',
            message: 'message.planNotFound',
            type: 'error',
          })
          return
        }

        payload['plan_id_list'].push(foundPlan.id)
        payload['subtotal'] = foundPlan.price
      } else if (this.addServiceData.type === 'update_corporate_info') {
        payload['plan_id_list'] = this.addServiceData.update_info_selected
        const plans = this.allPlansData.filter(el => this.addServiceData.update_info_selected.includes(el.id))
        plans.forEach(el => {
          payload['subtotal'] += el.price
        })
      } else {
        const foundPlan = this.allPlansData.find(el => el.type === this.addServiceData.type)
        if (!foundPlan) {
          this.$store.dispatch('setLoading', false)
          this.$refs['addServiceDialog'].setLoading(false)
          this.$store.dispatch('setAlert', {
            title: 'message.saveFail',
            message: 'message.planNotFound',
            type: 'error',
          })
          return
        }

        payload['plan_id_list'].push(foundPlan.id)
        payload['subtotal'] = foundPlan.price
      }

      if (
        this.addServiceData.type !== 'secretary' &&
        this.addServiceData.type !== 'virtual_office' &&
        this.addServiceData.type !== 'scr'
      ) {
        payload['company_name_zh'] = this.companyData['name_zh']
        payload['company_name_en'] = this.companyData['name_en']
        payload['contact_name'] = this.companyData['contact_en']
        payload['contact_email'] = this.companyData['contact_email']
        payload['contact_zone'] = this.companyData['contact_zone']
        payload['contact_phone'] = this.companyData['contact_tel']
        payload['company_number'] = this.companyData['br_number']
      }

      if (payload['payment_method'] === 'offline_credit_card') {
        payload['subtotal'] = Math.ceil((payload['subtotal'] * 104) / 100);
      }

      try {
        if (
          this.addServiceData.type === 'secretary' ||
          this.addServiceData.type === 'virtual_office' ||
          this.addServiceData.type === 'scr'
        ) {
          await this.$Fetcher.NewOrder(payload)
        } else {
          await this.$Fetcher.NewServiceSubmit(payload)
        }

        this.$store.dispatch('setLoading', false)
        this.$refs['addServiceDialog'].hide()
        this.$store.dispatch('toggleSnack', {
          type: 'success',
          message: 'message.saveSuccess',
          refresh: true,
        })
      } catch (err) {
        this.$common.error(err)
        this.$refs['addServiceDialog'].setLoading(false)
        this.$store.dispatch('setLoading', false)
        this.$store.dispatch('setAlert', {
          title: 'message.saveFail',
          message: err,
          type: 'error',
        })
      }
    },
  },

  async mounted() {
    this.$store.dispatch('setInitLoading', true)
    if (this.$validate.DataValid(this.id)) {
      this.getCompany()
    } else if (this.$validate.DataValid(this.uid)) {
      await this.getCompanyUser(this.uid)
      if (this.userData === null) {
        this.$store.dispatch('setAlert', {
          message: 'noData',
          redirect: 'UserList',
        })
      } else {
        this.formData.contact_zone = this.userData.zone || '852'
        this.formData.contact_tel = this.userData.phone
      }
      this.$store.dispatch('setInitLoading', false)
    } else {
      this.$store.dispatch('setInitLoading', false)
      this.$store.dispatch('setAlert', {
        message: 'noData',
        redirect: 'UserList',
      })
    }
  },
}
</script>
